import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import KeyboardDoubleArrowRightOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowRightOutlined';
import UserService from '../../services/user-service';
import User from '../../services/interfaces/user-interface';

import { RingLoader } from 'react-spinners';
import { AxiosInstance } from '../../common/Loader/axios.config';

interface UserAnalytics {
  totalUsers: number;
  activeUsers: number;
  pendingUsers: number;
  deactivatedUsers: number;
  newUsersToday: number;
  newUsersThisMonth: number;
}

interface StatCardProps {
  label: string;
  value: number;
}

const StatCard = ({ label, value }: StatCardProps) => (
  <div className="rounded-lg border border-stroke bg-white dark:bg-brand-background px-4 py-3 shadow-sm">
    <p className="text-sm text-bodydark2">{label}</p>

    <h4 className="mt-2 text-2xl font-bold text-black dark:text-white">
      {value.toLocaleString()}
    </h4>
  </div>
);

const UserAnayltics = () => {
  const [stats, setStats] = useState<UserAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    const fetchUserAnalytics = async () => {
      try {
        const response = await AxiosInstance.get('/users/a/analytics');

        if (response.data?.status === 'success') {
          setStats(response.data.message);
          console.log('User Analytics:', response.data.message);
        }
      } catch (error) {
        console.error('Error fetching user analytics:', error);
      }
    };

    fetchUserAnalytics();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await UserService.getLatest50Users();

        setUsers(response);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  return (
    <div className="rounded-xl border border-stroke bg-white px-5 mx-1 pb-2.5 pt-6 shadow-default dark:border-strokedark dark:bg-brand-background sm:px-7.5 xl:pb-1">
      <Link to="/manage?tab=1">
        <h4 className="mb-6 text-center text-xl text-black dark:text-white">
          User Analytics <KeyboardDoubleArrowRightOutlinedIcon />
        </h4>
      </Link>

      <div className="flex flex-col">
        <div className="mb-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <StatCard
              label="Total Users"
              value={stats?.totalUsers ?? 0}
            />

            <StatCard
              label="Active Users"
              value={stats?.activeUsers ?? 0}
            />

            <StatCard
              label="Pending Users"
              value={stats?.pendingUsers ?? 0}
            />

            <StatCard
              label="New Today"
              value={stats?.newUsersToday ?? 0}
            />

            <StatCard
              label="New This Month"
              value={stats?.newUsersThisMonth ?? 0}
            />

            <StatCard
              label="Deactivated"
              value={stats?.deactivatedUsers ?? 0}
            />
          </div>
        </div>

        {/* Recent Users Header */}
        <div className="mt-4 mb-2">
          <h5 className="text-lg font-semibold text-black dark:text-white">
            Recent Users
          </h5>
        </div>

        <div className="grid grid-cols-1 border-t border-stroke dark:border-strokedark sm:grid-cols-1">
          <div className="flex items-center justify-between px-4">
            <div className="p-2.5 text-right sm:block xl:p-2">
              <p className="text-sm uppercase tracking-wide text-bodydark2">
                User
              </p>
            </div>

            <div className="p-2.5 text-right sm:block xl:p-2">
              <p className="text-sm uppercase tracking-wide text-bodydark2">
                Created
              </p>
            </div>
          </div>
        </div>

        {/* Recent Users List */}
        {loading ? (
          <div className="mt-8 flex justify-center">
            <RingLoader size={70} color="#36D7B7" loading={loading} />
          </div>
        ) : (
          <div
            className="no-scrollbar"
            style={{ maxHeight: '290px', overflowY: 'scroll' }}
          >
            {users.map((user) => (
              <div
                key={user._id}
                className="grid grid-cols-2 border-t border-stroke dark:border-strokedark sm:grid-cols-2 hover:bg-gray-50 dark:hover:bg-meta-4 transition-colors"
              >
                <div className="flex items-center gap-3 p-2.5 xl:p-5">
                  <div>
                    <p className="font-medium text-black dark:text-white">
                      {user.email ?? user.phone}
                    </p>
                  </div>
                </div>

                <div className="justify-center p-2.5 text-right xl:p-5">
                  <p className="text-sm text-bodydark2">
                    {new Date(user.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserAnayltics;